import { useMemo, useState, useEffect } from "react";
import Papa from "papaparse";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Progress } from "@/components/ui/progress";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Upload, Table as TableIcon } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import BulkPasteTable from "./BulkPasteTable";
import ExamLabelPicker, { type ExamLabel } from "./ExamLabelPicker";

interface ParsedRow {
  question: string;
  category?: string;
  subCategory?: string;
  options: string[];
  correctAnswer: string;
  explanation?: string;
  tags?: string[];
}

interface Mapping {
  question?: string;
  category?: string;
  subCategory?: string;
  correctAnswer?: string;
  explanation?: string;
  tags?: string;
  optionColumns: string[];
}

const TEMPLATE_HEADERS = [
  "Question",
  "Category",
  "Sub-Category",
  "Tags",
  "Answer 1",
  "Answer 2",
  "Answer 3",
  "Answer 4",
  "Answer 5",
  "Correct Answer",
  "Explanation",
  "Source",
];

function normalize(s?: string) {
  return (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "").trim();
}

function defaultMapping(columns: string[]): Mapping {
  const by = (names: string[]): string | undefined => {
    const targets = names.map(normalize);
    return columns.find((c) => targets.includes(normalize(c)));
  };

  const question = by(["question", "q", "prompt"]);
  const category = by(["category", "cat", "topic", "subject"]);
  const subCategory = by(["subcategory", "subcat", "subtopic"]);
  const correctAnswer = by(["correctanswer", "answer", "correct"]);
  const explanation = by(["explanation", "rationale", "why"]);
  const tags = by(["tags", "keywords", "labels"]);

  // Option columns: any that look like Answer/Option, sorted by trailing number
  const optionCandidates = columns.filter((c) => /^(answer|option)/i.test(c) || /choice\s*\d+/i.test(c));
  const sortedOptions = optionCandidates
    .map((c) => ({ c, n: parseInt((c.match(/(\d+)/)?.[1] || "999"), 10) }))
    .sort((a, b) => a.n - b.n)
    .map((x) => x.c)
    .slice(0, 5);

  // Fallback: pick first 5 unused columns
  const used = new Set([question, category, subCategory, correctAnswer, explanation, tags].filter(Boolean) as string[]);
  const fallback = columns.filter((c) => !used.has(c)).slice(0, 5);
  const optionColumns = (sortedOptions.length ? sortedOptions : fallback).filter(Boolean);

  return { question, category, subCategory, correctAnswer, explanation, tags, optionColumns };
}

function buildRows(raw: any[], mapping: Mapping): ParsedRow[] {
  const m = mapping;
  return raw
    .map((r) => {
      const get = (col?: string) => (col ? (r[col] ?? "").toString().trim() : "");
      const options = (m.optionColumns || [])
        .map((col) => get(col))
        .filter((v) => v.length > 0);
      const tags = get(m.tags)
        .split(/[;,]/)
        .map((t) => t.trim())
        .filter(Boolean);
      return {
        question: get(m.question),
        category: get(m.category) || undefined,
        subCategory: get(m.subCategory) || undefined,
        options,
        correctAnswer: get(m.correctAnswer),
        explanation: get(m.explanation) || undefined,
        tags,
      } as ParsedRow;
    })
    .filter((row) => row.question && row.options.length >= 2 && row.correctAnswer);
}

export default function MCQImport() {
  const [rawRows, setRawRows] = useState<any[]>([]);
  const [columns, setColumns] = useState<string[]>([]);
  const [mapping, setMapping] = useState<Mapping>({ optionColumns: [] });
  const [fileName, setFileName] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [processed, setProcessed] = useState(0);
  const [upserted, setUpserted] = useState(0);
  const [skipped, setSkipped] = useState(0);
  const [errors, setErrors] = useState<Array<{ index: number; reason: string; question?: string }>>([]);
  const [examLabels, setExamLabels] = useState<ExamLabel[]>([]);
  const [selectedExamLabels, setSelectedExamLabels] = useState<string[]>([]);
  const [labelsAssigned, setLabelsAssigned] = useState(0);

  const rows = useMemo(() => buildRows(rawRows, mapping), [rawRows, mapping]);
  const total = rows.length;
  const preview = useMemo(() => rows.slice(0, 5), [rows]);
  const progress = total ? Math.round((processed / total) * 100) : 0;

  // Load exam labels
  useEffect(() => {
    const loadExamLabels = async () => {
      const { data, error } = await supabase
        .from("exam_labels")
        .select("id, slug, name")
        .order("name");
      if (error) {
        console.error("Failed to load exam labels:", error);
        return;
      }
      setExamLabels(data || []);
    };
    loadExamLabels();
  }, []);

  const onFile = (file?: File) => {
    if (!file) return;
    setFileName(file.name);
    setErrors([]);
    setProcessed(0);
    setUpserted(0);
    setSkipped(0);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (h) => h.trim(),
      complete: (res) => {
        const data: any[] = (res.data || []) as any[];
        const fields: string[] = (res.meta as any)?.fields || (data[0] ? Object.keys(data[0]) : []);
        setColumns(fields);
        setRawRows(data);
        const dm = defaultMapping(fields);
        setMapping(dm);
        const ready = buildRows(data, dm).length;
        toast({ title: "CSV parsed", description: `${ready} rows ready` });
      },
      error: (err) => {
        console.error("CSV parse error", err);
        toast({ title: "Failed to parse CSV", description: err.message, variant: "destructive" as any });
      },
    });
  };

  const resetMapping = () => {
    const dm = defaultMapping(columns);
    setMapping(dm);
  };

  const toggleOptionColumn = (col: string) => {
    setMapping((m) => {
      const exists = m.optionColumns.includes(col);
      return {
        ...m,
        optionColumns: exists ? m.optionColumns.filter((c) => c !== col) : [...m.optionColumns, col],
      };
    });
  };

  const handleImport = async () => {
    if (rows.length === 0) return;
    if (selectedExamLabels.length === 0) {
      toast({ title: "Please select exam labels", description: "At least one exam label is required", variant: "destructive" as any });
      return;
    }
    setLoading(true);
    setProcessed(0);
    setUpserted(0);
    setSkipped(0);
    setErrors([]);
    setLabelsAssigned(0);

    try {
      const chunkSize = 200;
      let totalUpserted = 0;
      let totalSkipped = 0;
      let totalLabelsAssigned = 0;
      for (let start = 0; start < rows.length; start += chunkSize) {
        const chunk = rows.slice(start, start + chunkSize);
        const { data, error } = await supabase.functions.invoke("import-mcqs", {
          body: { 
            records: chunk, 
            baseIndex: start,
            examLabelIds: selectedExamLabels
          },
        });
        if (error) throw error;
        setProcessed(Math.min(start + chunk.length, rows.length));
        totalUpserted += data?.upserted ?? 0;
        totalSkipped += data?.skipped ?? 0;
        totalLabelsAssigned += data?.labelsAssigned ?? 0;
        setUpserted(totalUpserted);
        setSkipped(totalSkipped);
        setLabelsAssigned(totalLabelsAssigned);
        if (Array.isArray(data?.errors)) {
          setErrors((prev) => [...prev, ...data.errors]);
        }
      }
      toast({ 
        title: "Import complete", 
        description: `Upserted ${totalUpserted} MCQs | Assigned ${totalLabelsAssigned} exam labels | Skipped ${totalSkipped}` 
      });
    } catch (e: any) {
      console.error(e);
      toast({ title: "Import failed", description: e?.message || "Unknown error", variant: "destructive" as any });
    } finally {
      setLoading(false);
    }
  };

  const downloadErrorsCsv = () => {
    if (errors.length === 0) return;
    const header = [
      "Row Index",
      "Reason",
      "Question",
      "Category",
      "Sub-Category",
      "Tags",
      "Correct Answer",
      "Options",
    ];
    const lines = [header.join(",")];
    errors
      .sort((a, b) => a.index - b.index)
      .forEach((e) => {
        const r = rows[e.index];
        const csvRow = [
          e.index,
          JSON.stringify(e.reason),
          JSON.stringify(r?.question ?? e.question ?? ""),
          JSON.stringify(r?.category ?? ""),
          JSON.stringify(r?.subCategory ?? ""),
          JSON.stringify((r?.tags || []).join(";")),
          JSON.stringify(r?.correctAnswer ?? ""),
          JSON.stringify((r?.options || []).join(" | ")),
        ].join(",");
        lines.push(csvRow);
      });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "mcq_import_errors.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Tabs defaultValue="file-upload" className="w-full">
      <TabsList className="grid w-full grid-cols-2">
        <TabsTrigger value="file-upload" className="flex items-center gap-2">
          <Upload className="h-4 w-4" />
          CSV File Upload
        </TabsTrigger>
        <TabsTrigger value="bulk-paste" className="flex items-center gap-2">
          <TableIcon className="h-4 w-4" />
          Bulk Paste Table
        </TabsTrigger>
      </TabsList>

      <TabsContent value="file-upload" className="mt-6">
        <Card>
          <CardContent className="p-6 space-y-5 animate-fade-in">
            <div className="rounded-lg border bg-muted/30 p-4 space-y-2">
              <p className="font-medium">How to import</p>
              <ol className="list-decimal list-inside text-sm text-muted-foreground space-y-1">
                <li>Download the CSV template and review the headers.</li>
                <li>Fill in your questions, options, and the correct answer.</li>
                <li>Upload the CSV file below and start the import.</li>
              </ol>
              <div>
                <a className="underline" href="/mcq_template.csv" download>
                  Download CSV template
                </a>
              </div>
            </div>

            {/* Exam Labels Selection */}
            {examLabels.length > 0 && (
              <div className="space-y-3">
                <ExamLabelPicker
                  labels={examLabels}
                  value={selectedExamLabels}
                  onChange={setSelectedExamLabels}
                />
                {selectedExamLabels.length > 0 && (
                  <p className="text-sm text-muted-foreground">
                    {rows.length > 0 ? `${rows.length} MCQs will be assigned to ${selectedExamLabels.length} exam(s)` : "Select a CSV file to import"}
                  </p>
                )}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3">
              <Input type="file" accept=".csv" onChange={(e) => onFile(e.target.files?.[0])} />
              <Button 
                onClick={handleImport} 
                disabled={rows.length === 0 || loading || selectedExamLabels.length === 0} 
                className="hover-scale"
              >
                {loading ? `Importing… ${progress}%` : `Import ${rows.length} MCQs`}
              </Button>
              {errors.length > 0 && (
                <Button variant="secondary" onClick={downloadErrorsCsv} className="hover-scale">
                  Download errors CSV ({errors.length})
                </Button>
              )}
            </div>
            {fileName && (
              <p className="text-sm text-muted-foreground">Parsed from: {fileName}</p>
            )}

            {columns.length > 0 && (
              <div className="space-y-3">
                <p className="font-medium">Column mapping</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <Label>Question</Label>
                    <Select value={mapping.question || "none"} onValueChange={(v) => setMapping((m) => ({ ...m, question: v === "none" ? undefined : v }))}>
                      <SelectTrigger><SelectValue placeholder="Select column" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {columns.filter(c => c && c.trim() !== '').map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label>Category</Label>
                    <Select value={mapping.category || "none"} onValueChange={(v) => setMapping((m) => ({ ...m, category: v === "none" ? undefined : v }))}>
                      <SelectTrigger><SelectValue placeholder="Select column" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {columns.filter(c => c && c.trim() !== '').map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label>Sub-Category</Label>
                    <Select value={mapping.subCategory || "none"} onValueChange={(v) => setMapping((m) => ({ ...m, subCategory: v === "none" ? undefined : v }))}>
                      <SelectTrigger><SelectValue placeholder="Select column" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {columns.filter(c => c && c.trim() !== '').map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label>Correct Answer</Label>
                    <Select value={mapping.correctAnswer || "none"} onValueChange={(v) => setMapping((m) => ({ ...m, correctAnswer: v === "none" ? undefined : v }))}>
                      <SelectTrigger><SelectValue placeholder="Select column" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {columns.filter(c => c && c.trim() !== '').map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label>Explanation</Label>
                    <Select value={mapping.explanation || "none"} onValueChange={(v) => setMapping((m) => ({ ...m, explanation: v === "none" ? undefined : v }))}>
                      <SelectTrigger><SelectValue placeholder="Select column" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {columns.filter(c => c && c.trim() !== '').map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label>Tags</Label>
                    <Select value={mapping.tags || "none"} onValueChange={(v) => setMapping((m) => ({ ...m, tags: v === "none" ? undefined : v }))}>
                      <SelectTrigger><SelectValue placeholder="Select column" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {columns.filter(c => c && c.trim() !== '').map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Option columns (select in the order to use)</Label>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {columns.map((c) => (
                      <label key={c} className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={mapping.optionColumns.includes(c)}
                          onCheckedChange={() => toggleOptionColumn(c)}
                        />
                        <span className="text-sm">{c}</span>
                      </label>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    Selected: {mapping.optionColumns.length} column(s)
                    <Button variant="ghost" size="sm" onClick={resetMapping}>Reset mapping</Button>
                  </div>
                </div>
              </div>
            )}

            {loading && (
              <div className="space-y-2">
                <Progress value={progress} />
                <div className="text-sm text-muted-foreground">
                  Processed {processed}/{total} · Upserted {upserted} · Exam labels assigned {labelsAssigned} · Skipped {skipped}
                </div>
              </div>
            )}

            {rows.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Preview of first {preview.length} rows</p>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Question</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Sub-Category</TableHead>
                        <TableHead>Options</TableHead>
                        <TableHead>Correct</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {preview.map((r, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="max-w-[320px] truncate" title={r.question}>{r.question}</TableCell>
                          <TableCell>{r.category || "—"}</TableCell>
                          <TableCell>{r.subCategory || "—"}</TableCell>
                          <TableCell>{r.options.length}</TableCell>
                          <TableCell className="max-w-[220px] truncate" title={r.correctAnswer}>{r.correctAnswer}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            <div className="text-xs text-muted-foreground">
              Template headers: {TEMPLATE_HEADERS.join(", ")}
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="bulk-paste" className="mt-6">
        <BulkPasteTable />
      </TabsContent>
    </Tabs>
  );
}
